import { ILinkedModel } from "../../../responses/linkedModel";
import { LinkedModelsDockingPanel } from "../docking-panels/linkedModelsDockingPanel";
import { LinkedModel } from "../linkedModels/linkedModel";
import { settingsToolsToolbarGroupId, measureToolsToolbarGroupId } from "../toolbar/toolbarGroupIds";
import { ViewerModelCategoriesMap } from "../viewer-utils/viewerModelCollector";
import {
    ChangeLinkedModelVisibilityEventPayload,
    LinkedModelView,
    UpdateLinkedModelOriginEventPayload,
} from "../eventBus/linkedModelsEventPayloads";
import eventBus, { IApplicationEvent } from "../eventBus/eventDispatcher";
import repo from "../../../Repository";

export type ForgeLinkedModelsExtensionLoadOptions = {
    modelId: string;
    projectId: string;
};

export class ForgeLinkedModelsExtension extends Autodesk.Viewing.Extension {
    private dockingPanel: LinkedModelsDockingPanel | null = null;
    private linkedModelsEditorToggleButton: Autodesk.Viewing.UI.Button | null = null;
    private readonly linkedModels: LinkedModel[];
    private readonly projectId: string;
    private readonly modelId: string;
    private readonly hiddenRevitCategories: number[];
    private readonly needToLoadLinkedModels: boolean;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D, options: ForgeLinkedModelsExtensionLoadOptions) {
        super(viewer, options);

        const categoriesMap = new ViewerModelCategoriesMap();

        this.hiddenRevitCategories = [categoriesMap.findCategoryId("Levels")!];
        this.raiseLinkedModelsLoadedEvent = this.raiseLinkedModelsLoadedEvent.bind(this);

        this.linkedModels = [];
        this.needToLoadLinkedModels = true;

        this.projectId = options.projectId;
        this.modelId = options.modelId;

        this.onLinkedModelCreated = this.onLinkedModelCreated.bind(this);
        this.onAutomaticPanelsLayoutToggled = this.onAutomaticPanelsLayoutToggled.bind(this);
        this.onLinkedModelRemoved = this.onLinkedModelRemoved.bind(this);
        this.onToggleLinkedModelsEditor = this.onToggleLinkedModelsEditor.bind(this);
        this.onChangeLinkedModelVisibility = this.onChangeLinkedModelVisibility.bind(this);
        this.onHighlightLinkedModel = this.onHighlightLinkedModel.bind(this);
        this.onLinkedModelOffsetUpdated = this.onLinkedModelOffsetUpdated.bind(this);
        this.onDockingPanelVisibilityChanged = this.onDockingPanelVisibilityChanged.bind(this);

        this.onEscape = this.onEscape.bind(this);
    }

    async load() {
        if (this.needToLoadLinkedModels) {
            const response = await repo.findLinkedModels(this.modelId);

            if (response.isSuccess) {
                const linkedModels = response.items.map(
                    (x: ILinkedModel) =>
                        new LinkedModel(
                            x,
                            this.viewer,
                            this.hiddenRevitCategories,
                            this.raiseLinkedModelsLoadedEvent,
                        ),
                );

                this.linkedModels.splice(0, 0, ...linkedModels);
            }
        }

        for (const linkedModel of this.linkedModels) {
            linkedModel.load();
        }

        eventBus.addEventListener("Dextall.LinkedModels.Created", this.onLinkedModelCreated);
        eventBus.addEventListener("Dextall.LinkedModels.ModelRemoved", this.onLinkedModelRemoved);
        eventBus.addEventListener("Dextall.Automation.PanelsLayout.Toggled", this.onAutomaticPanelsLayoutToggled);
        eventBus.addEventListener("Dextall.LinkedModels.UI.DockingPanelVisibilityChanged", this.onDockingPanelVisibilityChanged);
        eventBus.addEventListener("Dextall.LinkedModels.UI.ChangeVisibility", this.onChangeLinkedModelVisibility);
        eventBus.addEventListener("Dextall.LinkedModels.UI.Highlight", this.onHighlightLinkedModel);
        eventBus.addEventListener("Dextall.LinkedModels.UI.UpdateOrigin", this.onLinkedModelOffsetUpdated);
        this.viewer.addEventListener(Autodesk.Viewing.ESCAPE_EVENT, this.onEscape);

        return true;
    }

    unload() {
        this.viewer.removeEventListener(Autodesk.Viewing.ESCAPE_EVENT, this.onEscape);

        for (const linkedModel of this.linkedModels) {
            linkedModel.dispose();
        }

        this.linkedModels.length = 0;

        eventBus.removeEventListener("Dextall.LinkedModels.Created", this.onLinkedModelCreated);
        eventBus.removeEventListener("Dextall.LinkedModels.ModelRemoved", this.onLinkedModelRemoved);
        eventBus.removeEventListener("Dextall.Automation.PanelsLayout.Toggled", this.onAutomaticPanelsLayoutToggled);
        eventBus.removeEventListener("Dextall.LinkedModels.UI.DockingPanelVisibilityChanged", this.onDockingPanelVisibilityChanged);
        eventBus.removeEventListener("Dextall.LinkedModels.UI.ChangeVisibility", this.onChangeLinkedModelVisibility);
        eventBus.removeEventListener("Dextall.LinkedModels.UI.Highlight", this.onHighlightLinkedModel);
        eventBus.removeEventListener("Dextall.LinkedModels.UI.UpdateOrigin", this.onLinkedModelOffsetUpdated);

        this.dockingPanel?.shutdown();
        this.dockingPanel = null;

        this.linkedModelsEditorToggleButton?.removeEventListener("click", this.onToggleLinkedModelsEditor);
        this.linkedModelsEditorToggleButton = null;

        return true;
    }

    onToolbarCreated() {
        const toolbar = this.viewer.toolbar;

        let group = toolbar.getControl(settingsToolsToolbarGroupId) as Autodesk.Viewing.UI.ControlGroup | undefined;

        if (!group) {
            const measureTools = toolbar.getControl(measureToolsToolbarGroupId) as
                | Autodesk.Viewing.UI.ControlGroup
                | undefined;

            if (!measureTools) {
                toolbar.addControl(new Autodesk.Viewing.UI.ControlGroup(measureToolsToolbarGroupId), { index: 1 });
            }

            group = new Autodesk.Viewing.UI.ControlGroup(settingsToolsToolbarGroupId);
            toolbar.addControl(group, { index: toolbar.indexOf(measureToolsToolbarGroupId) });
        }

        this.linkedModelsEditorToggleButton = new Autodesk.Viewing.UI.Button("dextall-editor-linked-models-button");
        this.linkedModelsEditorToggleButton.setToolTip("Linked models");
        this.linkedModelsEditorToggleButton.setIcon("viewer-edit-linked-models");

        this.linkedModelsEditorToggleButton.addEventListener("click", this.onToggleLinkedModelsEditor);

        group.addControl(this.linkedModelsEditorToggleButton, { index: 1 });

        this.dockingPanel = new LinkedModelsDockingPanel(this.viewer.container, this.modelId, this.projectId);
    }

    private onEscape() {
        this.dockingPanel?.setVisible(false);
    }

    private onToggleLinkedModelsEditor() {
        const newState = !this.dockingPanel?.visible;

        this.dockingPanel?.setVisible(newState);
    }

    private onDockingPanelVisibilityChanged(event: IApplicationEvent<boolean>) {
        if (!(this.dockingPanel && this.linkedModelsEditorToggleButton)) {
            return;
        }

        if (event.payload) {
            this.raiseLinkedModelsLoadedEvent();
        }

        this.linkedModelsEditorToggleButton.setState(
            event.payload ? Autodesk.Viewing.UI.Button.State.ACTIVE : Autodesk.Viewing.UI.Button.State.INACTIVE,
        );
    }

    private onLinkedModelCreated(event: IApplicationEvent<ILinkedModel>) {
        const linkedModel = new LinkedModel(
            event.payload,
            this.viewer,
            this.hiddenRevitCategories,
            this.raiseLinkedModelsLoadedEvent,
        );

        this.linkedModels.push(linkedModel);

        linkedModel.load();

        this.raiseLinkedModelsLoadedEvent();
    }

    private onLinkedModelRemoved(event: IApplicationEvent<string>) {
        const linkedModelIndex = this.linkedModels.findIndex(x => x.id === event.payload);

        if (linkedModelIndex < 0) {
            return;
        }

        const linkedModel = this.linkedModels[linkedModelIndex];

        linkedModel.unload();
        this.linkedModels.splice(linkedModelIndex, 1);
        this.raiseLinkedModelsLoadedEvent();
    }

    private onAutomaticPanelsLayoutToggled(event: IApplicationEvent<boolean>) {
        const isModelVisible = !event.payload;

        for (const linkedModel of this.linkedModels) {
            linkedModel.setVisible(isModelVisible);
        }
    }

    private onChangeLinkedModelVisibility(event: IApplicationEvent<ChangeLinkedModelVisibilityEventPayload>) {
        const { id, visible } = event.payload;
        const linkedModel = this.linkedModels.find(x => x.id === id);

        if (!linkedModel) {
            return;
        }

        linkedModel.setVisible(visible);
        this.raiseLinkedModelsLoadedEvent();
    }

    private onHighlightLinkedModel(event: IApplicationEvent<string>) {
        const linkedModel = this.linkedModels.find(x => x.id === event.payload);

        linkedModel?.highlight();
    }

    private onLinkedModelOffsetUpdated(event: IApplicationEvent<UpdateLinkedModelOriginEventPayload>) {
        const { id, origin } = event.payload;
        const linkedModel = this.linkedModels.find(x => x.id === id);

        linkedModel?.setOrigin(origin);
        this.raiseLinkedModelsLoadedEvent();
    }

    private raiseLinkedModelsLoadedEvent() {
        const linkedModels = this.linkedModels.map(
            (x): LinkedModelView => ({
                id: x.id,
                fileName: x.fileName,
                status: x.status,
                translationProgressPercentage: x.translationProgressPercentage,
                createdAt: x.createdAt,
                isVisible: x.isVisible,
                isLoaded: x.loaded,
                origin: x.origin,
            }),
        );

        eventBus.dispatchEvent({
            type: "Dextall.LinkedModels.Loaded",
            payload: linkedModels,
        });
    }
}

export const forgeLinkedModelsExtensionName = "Dextall.ForgeLinkedModelsExtension" as const;

Autodesk.Viewing.theExtensionManager.registerExtension(forgeLinkedModelsExtensionName, ForgeLinkedModelsExtension);
